import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { CustomEvent } from 'src/app/parameters/custom-events/custom-event.model';
import { RoleName, UserV2 } from 'src/app/parameters/users/shared/models';
import { SiteV2 } from 'src/app/shared/models';
import { Company } from 'src/app/shared/models/company';
import { BasicService } from 'src/app/shared/services/basic.service';
import { StrategiesService } from 'src/app/shared/strategies/strategies.service';
import {
  Metric,
  MetricGroupBy,
  MetricSelector,
  Report,
  ReportFilter,
  ReportLabels,
  ScenarioSimpleInfo,
} from '../../../models';
import { ReportFilters } from '../../../models/report-filters.model';

@Component({
  selector: 'app-report-edition-metric-dialog',
  templateUrl: '../report-edition-dialog.component.html',
  styleUrls: ['../report-edition-dialog.component.scss'],
})
export class ReportEditionMetricDialogComponent implements OnInit {
  //data
  customEvents: CustomEvent[];
  dialogTitle: string;
  isCreation: boolean;
  metric: Metric;
  report: Report;
  roles: RoleName[];
  scenarios: ScenarioSimpleInfo[];
  sites: SiteV2[];

  careScenarios: ScenarioSimpleInfo[];
  company: Company;
  marketingScenarios: ScenarioSimpleInfo[];
  surveyScenarios: ScenarioSimpleInfo[];
  users: UserV2[];

  //form
  clickedSave: Subject<void> = new Subject();
  isFormValid: boolean;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    public dialogRef: MatDialogRef<ReportEditionMetricDialogComponent>,
    private basicService: BasicService,
    private strategiesService: StrategiesService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.customEvents = this.data.customEvents;
    this.dialogTitle = this.data.dialogTitle;
    this.isCreation = this.data.isCreation;
    this.metric = this.data.metric;
    this.report = this.data.report;
    this.roles = this.data.roles;
    this.scenarios = this.data.scenarios;
    this.sites = this.data.sites;

    this.users = this.alcmeonContextService.self.companies[0].users;
    this.careScenarios = this.scenarios.filter(s => s.flavor === 'quick-replies');
    this.surveyScenarios = this.scenarios.filter(s => s.flavor === 'satisfaction-survey');
    this.marketingScenarios = this.scenarios.filter(s => s.flavor === 'campaign-scenarios');
    this.company = this.alcmeonContextService.getCompany();

    this.addUIFields(this.report, this.metric);
  }

  /**
   * Adds new property to the report.ui to display: metricName, filterCriteria, groubBy
   * and for the form : dateCriteria
   */
  private addUIFields(report: Report, metric: Metric) {
    if (!report.ui) {
      report.ui = {};
    }
    report.ui.metricName = metric.name;

    if (Object.keys(report.filters).length) {
      const filterLabels = this.getLabelsForEachSelectorType(metric);
      report.ui.filterCriteria = this.getFiltersForUI(report.filters, filterLabels);
      const scenarioCriteria = this.getScenarioForUI(report.filters);
      report.ui.filterCriteria = report.ui.filterCriteria.concat(scenarioCriteria);
    } else {
      report.ui.filterCriteria = [];
    }

    if (report.group_by) {
      report.ui.groupBy = this.getGroupByForUI(report.group_by, metric);
    }

    // For the form
    report.ui.dateCriteria = [];
    metric.selectors_groups.forEach(selectorGroup =>
      selectorGroup.selectors
        .filter(selector => selector.type === 'dayrange')
        .forEach(selector => {
          const label = this.basicService.translate('metrics/' + metric.internal_name + '/' + selector.name);
          report.ui.dateCriteria.push({ selectorName: selector.name, label: label });
        })
    );
  }

  /**
   * @param metric
   * @returns object that contains all the selectors with data that we have in metric
   */
  private getLabelsForEachSelectorType(metric: Metric): {
    [where_flavor: string]: {
      [lvalue: string]: {
        selectorLabel: string;
        valueLabels: { [id: number]: string };
      };
    };
  } {
    const labels = {};
    metric.selectors_groups.forEach(selectorGroup =>
      selectorGroup.selectors.forEach(selector => {
        if (selector.fields_prefixes) {
          selector.fields = selector.fields_prefixes;
        }

        if (selector.fields === undefined) {
          const defaultFieldForSelectorType = {
            dayrange: 'where:' + selector.name,
            account: 'where:account',
            task_type: 'where:task_type',
            user: 'where:user',
            tags: 'where:task_tag_',
            scenario: 'where_clicks:scenario_button',
            human_or_bot: 'where:is_bot',
            'action-routing-tags': 'where:action_routing_tag',
            'incoming-team-tags': 'where:incoming_team_tag',
            scenarios: 'where:scenarios',
            roles: 'where:action_role',
            'custom-event': 'where:custom_event',
            'survey-scenario': 'where:survey_scenario',
            'manual-or-automatic': 'where:manual_or_automatic',
            'time-to-answer-survey': 'where_timeToAnswerSurvey:time_to_answer_survey',

            // for 'enum', 'bh_nbh' and 'qr-scenario', the 'fields' property is always present as there is no default that makes sense for all metrics
            // 'dayrange' fields are never present in report filters (they are set to a different range every day/week/month etc when the report is generated)
          };
          selector.fields = [defaultFieldForSelectorType[selector.type]];
        }

        // If there are multiple fields for a given selector, a user selection on that selector
        // will be converted to multiple entries in the 'filters' section of the /query POST body;
        // whereas we only want to show one entry in the report dialog.
        // Hence the [0] below to use at most one of the fields for that selector
        const whereFlavorAndField = selector.fields[0];
        const whereFlavor = whereFlavorAndField.split(':')[0];
        let fieldName = whereFlavorAndField.split(':')[1];

        if (labels[whereFlavor] === undefined) {
          labels[whereFlavor] = {};
        }

        if (fieldName.includes('task_tag_')) {
          this.addTagLabels(labels[whereFlavor], fieldName);
        } else if (['action_routing_tag', 'incoming_team_tag', 'team'].includes(fieldName)) {
          this.addRoutingTagLabels(labels[whereFlavor], fieldName);
        } else {
          if (selector.type === 'bh_nbh') {
            // there could be a dayrange selector on the same field
            // (at the moment we remove dayranges from filters when creating a report but just in case this changes in the future)
            // Let's rename the field in the labels
            fieldName += '_bh_nbh';
          }

          const selectorDescription = {
            selectorLabel: this.getSelectorLabel(selector, metric),
            valueLabels: this.getValueLabels(selector),
          };

          labels[whereFlavor][fieldName] = selectorDescription;
        }
      })
    );

    return labels;
  }

  /**
   * Adds data for tags: task_tag_1, task_tag_2 etc. ex: language, Team (message tag) and tags created by user
   * @param labelsForWhereFlavor example: { task_tag_2: {selectorLabel: 'Team (message tag', valueLabels: {26: 'None', 27: 'Team1'}}}
   * @param fieldPrefix
   */
  private addTagLabels(labelsForWhereFlavor: ReportLabels, fieldPrefix: string) {
    this.company.tags_groups.forEach(tagGroup => {
      let tagGroupName = tagGroup.name;
      if (tagGroup.is_routing) {
        tagGroupName = this.basicService.translate('metrics/routing_tag_now');
      }

      const fieldName = `${fieldPrefix}${tagGroup.tag_index + 1}`;
      const selectorDescription = {
        selectorLabel: tagGroupName,
      };
      const labelValues = {};
      let currentCategory = null;

      tagGroup.tags_and_categories.forEach(tag => {
        if (tag.is_category) {
          currentCategory = tag.name;
        } else {
          labelValues[tag.id] = (currentCategory !== null ? `${currentCategory} ` : '') + tag.name;
        }
      });

      selectorDescription['valueLabels'] = labelValues;
      labelsForWhereFlavor[fieldName] = selectorDescription;
    });
  }

  /**
   * Adds data for team tags ex: incoming team tag , team etc.
   */
  private addRoutingTagLabels(labelsForWhereFlavor: ReportLabels, fieldPrefix: string) {
    this.company.tags_groups
      .filter(tagGroup => tagGroup.is_routing)
      .forEach(tagGroup => {
        let tagGroupName = tagGroup.name;
        if (tagGroup.is_routing) {
          if (fieldPrefix === 'action_routing_tag') {
            tagGroupName = this.basicService.translate('metrics/routing_tag_on_action');
          } else if (fieldPrefix === 'incoming_team_tag') {
            tagGroupName = this.basicService.translate('metrics/incoming-team-tag');
          }
        } else {
          tagGroupName = this.basicService.translate('metrics/routing_tag_now');
        }

        const fieldName = fieldPrefix;
        const selectorDescription = {
          selectorLabel: tagGroupName,
        };
        const labelValues = {};
        let currentCategory = null;

        tagGroup.tags_and_categories.forEach(tag => {
          if (tag.is_category) {
            currentCategory = tag.name;
          } else {
            labelValues[tag.id] = (currentCategory !== null ? `${currentCategory} ` : '') + tag.name;
          }
        });

        selectorDescription['valueLabels'] = labelValues;
        labelsForWhereFlavor[fieldName] = selectorDescription;
      });
  }

  /**
   * @returns translated name of the selector
   */
  private getSelectorLabel(selector: MetricSelector, metric: Metric): string {
    switch (selector.type) {
      case 'account':
        return this.basicService.translate('metrics/account');
      case 'custom-event':
        return this.basicService.translate('metrics/custom-event');
      case 'custom-field':
        return selector.display_name;
      case 'incoming-team-tags':
        return this.basicService.translate('metrics/incoming-team-tag');
      case 'manual-or-automatic':
        return this.basicService.translate('metrics/survey/manual_or_automatic');
      case 'roles':
        return this.basicService.translate('metrics/roles');
      case 'survey-scenario':
        return this.basicService.translate('metrics/survey/survey_scenario');
      case 'task_type':
        return this.basicService.translate('metrics/task_type');
      case 'time-to-answer-survey':
        return this.basicService.translate('metric/days_to_answer_survey');
      case 'bh_nbh':
      case 'qr-scenario':
        return this.basicService.translate('metrics/task-routing-tags');
      default:
        return this.basicService.translate('metrics/' + metric.internal_name + '/' + selector.name);
    }
  }

  /**
   * @returns values (sometimes translated) for selectorType
   */
  private getValueLabels(selector: MetricSelector): { [key: string]: string } {
    const labelValues = {};

    switch (selector.type) {
      case 'account':
        this.sites.forEach(site => (labelValues[site.id] = site.name));
        break;
      case 'bh_nbh':
        labelValues['business-hours'] = this.basicService.translate('metrics/bhnbh/bh');
        labelValues['non-business-hours'] = this.basicService.translate('metrics/bhnbh/nbh');
        break;
      case 'custom-event':
        this.customEvents.forEach(customEvent => (labelValues[customEvent.id] = customEvent.name));
        break;
      case 'custom-field':
        // no mapping necessary, as we have values directly
        break;
      case 'human_or_bot':
        labelValues[0] = this.basicService.translate('metrics/human_or_bot/humans_only');
        labelValues[1] = this.basicService.translate('metrics/human_or_bot/bots_only');
        break;
      case 'linked-to-crm-yes-no':
        labelValues['not null'] = this.basicService.translate('yes');
        labelValues['null'] = this.basicService.translate('no');
        break;
      case 'manual-or-automatic':
        labelValues[0] = this.basicService.translate('metrics/manual_or_automatic/automatic');
        labelValues[1] = this.basicService.translate('metrics/manual_or_automatic/manual');
        break;
      case 'qr-scenario':
        // the scenario filter is handled separetely by scenarioForUI()
        break;
      case 'roles':
        this.roles.forEach(role => (labelValues[role.id] = role.name));
        break;
      case 'scenarios':
        this.careScenarios
          .concat(this.marketingScenarios)
          .forEach(scenario => (labelValues[scenario.id] = scenario.name));
        break;
      case 'survey-scenario':
        this.surveyScenarios.forEach(scenario => (labelValues[scenario.id] = scenario.name));
        break;
      case 'task_type':
        this.addTaskTypesToLabelValues(labelValues);
        break;
      case 'time-to-answer-survey':
        labelValues[1] = this.basicService.translate('1');
        labelValues[2] = this.basicService.translate('2');
        labelValues[5] = this.basicService.translate('5');
        labelValues[10] = this.basicService.translate('10');
        labelValues[30] = this.basicService.translate('30');
        labelValues[60] = this.basicService.translate('60');
        break;
      case 'user':
        this.users.forEach(user => (labelValues[user.id] = user.realname));
        break;
      default:
      // defaulting to empty label map, UI will tell that details are not available for that filter
    }

    return labelValues;
  }

  /**
   * Adds names of the channels ex: Instagram Story Mention
   * @param labelValues
   */
  private addTaskTypesToLabelValues(labelValues: { [id: string]: string }) {
    this.strategiesService.getAll().forEach(strategie => {
      this.sites.find(site => {
        if (site.type === strategie.getType()) {
          strategie.getAllTasksTypes().forEach(taskType => {
            labelValues[taskType] = `${this.basicService.translate(
              'account_type/' + strategie.getTypeString()
            )} ${this.basicService.translate('task/' + strategie.getTaskTypeString(taskType))}`;
          });
          return true;
        }
      });
    });
  }

  /**
   * Takes all of the data and changes it to be good for display
   * @param filtersForBackend
   * @param filterLabels
   * @returns example {selectorType: 'Language', valueLabels: ['French', 'English', 'Italian']}
   */
  private getFiltersForUI(
    filtersForBackend: ReportFilters,
    filterLabels: { [whereFlavor: string]: ReportLabels }
  ): { selectorLabel: string; valueLabels: string[] }[] {
    const result = [];

    Object.keys(filtersForBackend).forEach(whereFlavor => {
      if (filterLabels[whereFlavor]) {
        const labelsForWhereFlavor = filterLabels[whereFlavor];

        filtersForBackend[whereFlavor].forEach((filterForBackend: ReportFilter[][]) => {
          if (
            filterForBackend.length > 0 &&
            filterForBackend[0].length > 0 &&
            filterForBackend[0][0].lvalue !== 'scenario_button'
          ) {
            // Let's extract the lvalue field first; the lvalue is the same for all elements of a filter
            let lvalue = filterForBackend[0][0].lvalue;
            if (filterForBackend[0][0].op === 'in_bh_nbh') {
              lvalue += '_bh_nbh';
            }

            if (labelsForWhereFlavor[lvalue]) {
              const labelsForLValue = labelsForWhereFlavor[lvalue];
              const filterForUI = {
                selectorLabel: labelsForLValue.selectorLabel,
                valueLabels: [],
              };

              // Conditions are grouped at a different depth in the array of arrays of arrays structure
              // depending on whether they should be ORed or ANDed together.
              // Here we don't deal with dayrange, only with "in" type of filter;
              // rvalues seem to be alone in a one-element array in that case
              filterForBackend.forEach((orExpression: ReportFilter[]) =>
                orExpression.forEach((andExpression: ReportFilter) => {
                  let rvalue: string | number | [number];
                  if (Array.isArray(andExpression.rvalue)) {
                    rvalue = andExpression.rvalue[0];
                  } else {
                    rvalue = andExpression.rvalue;
                  }

                  // || rvalue is for custom field where we have the value directly
                  const uiLabel = labelsForLValue.valueLabels[rvalue] || rvalue;
                  if (uiLabel) {
                    filterForUI.valueLabels.push(uiLabel);
                  }
                })
              );

              result.push(filterForUI);
            }
          }
        });
      }
    });

    return result;
  }

  /**
   * Transforms data to display for scenario selector
   * @param reportFilters
   * @returns criteria ready to display
   */
  private getScenarioForUI(reportFilters: ReportFilters): { selectorLabel: string; valueLabels: string[] }[] {
    let where_clauses = reportFilters.where;
    if (reportFilters.where_clicks) {
      where_clauses = reportFilters.where_clicks;
    } else {
      return [];
    }

    const scenariosFilters = where_clauses.filter(whereClause => whereClause[0][0].lvalue === 'scenario_button');
    const retval = [];
    const name_filter = scenariosFilters[0];
    if (name_filter) {
      const rvalue = name_filter[0][0].rvalue[0];
      const scenario_name = this.getScenarioName(rvalue);
      retval.push({
        selectorLabel: this.basicService.translate('parameters/quick-replies/report/scenario_name'),
        valueLabels: [scenario_name],
      });
    }
    return retval;
  }

  /**
   * @param rvalue type of filter
   * @returns scenario name
   */
  private getScenarioName(rvalue: string): string {
    let scenario_name: string;
    for (const s of this.careScenarios) {
      const s_name = s.name;
      if (s.quick_reply_answers && s.quick_reply_answers.includes(rvalue)) {
        scenario_name = s_name;
      }
    }
    return scenario_name;
  }

  /**
   * @returns translated name for groupBy
   */
  private getGroupByForUI(reportGroupBy: MetricGroupBy, metric: Metric): string {
    let fieldLabel = '';
    const tagLabels = {};
    this.addTagLabels(tagLabels, 'task_tag_');
    this.addRoutingTagLabels(tagLabels, 'incoming_team_tag');
    this.addRoutingTagLabels(tagLabels, 'action_routing_tag');

    if (reportGroupBy.type === 'timestamp') {
      // We need to lookup the groupBy name in the metric description
      metric.group_by.forEach(metricGroupBy => {
        if (reportGroupBy.type === 'timestamp' && reportGroupBy.timestamp_field === metricGroupBy.timestamp_field) {
          fieldLabel = this.basicService.translate(
            'metrics/group-by/' + metric.internal_name + '/' + metricGroupBy.name
          );
        }
      });

      if (reportGroupBy.subtype) {
        fieldLabel += ` / ${this.basicService.translate('metrics/group-by-timestamp/' + reportGroupBy.subtype)}`;
      }
    } else if (reportGroupBy.field.includes('task_tag_')) {
      fieldLabel = tagLabels[reportGroupBy.field].selectorLabel;
    } else if (reportGroupBy.field.includes('custom_field_')) {
      fieldLabel = reportGroupBy.displayName;
    } else {
      switch (reportGroupBy.field) {
        case 'account':
          fieldLabel = this.basicService.translate('metrics/group-by/account');
          break;
        case 'action_routing_tag':
          fieldLabel = tagLabels[reportGroupBy.field].selectorLabel;
          break;
        case 'crm_link':
          fieldLabel = this.basicService.translate('metrics/group-by/user_linked_to_crm');
          break;
        case 'custom_event':
          fieldLabel = this.basicService.translate('metrics/group-by/' + metric.internal_name + '/' + 'custom-event');
          break;
        case 'incoming_team_tag':
          fieldLabel = tagLabels[reportGroupBy.field].selectorLabel;
          break;
        case 'scenarios':
          fieldLabel = this.basicService.translate('metrics/group-by/' + metric.internal_name + '/' + 'scenarios');
          break;
        case 'task_type':
          fieldLabel = this.basicService.translate('metrics/group-by/task_type');
          break;
        case 'user':
          fieldLabel = this.basicService.translate('metrics/group-by/' + metric.internal_name + '/' + 'user');
          break;
        default:
          fieldLabel = reportGroupBy.field;
      }
    }

    return fieldLabel;
  }

  changeFormValidation(formValid: boolean) {
    this.isFormValid = formValid;
  }

  saveForm() {
    this.clickedSave.next();
  }
}
